import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO, eachDayOfInterval } from "date-fns";
import { API } from "../App";
import { fmtDateMed, fmtDayMonth, parseYMD } from "../lib/dates";
import { ROSTER_LAYOUTS } from "../components/RosterLayoutPicker";

const LEAVE_COUNTS_FALLBACK = { annual: true, sick: true, personal: true };

/**
 * Encapsulates all calendar-grid state + derived data + handlers for RosterDetail:
 * layout/view mode, visible date window, drag-and-drop reassignment, add/delete entry,
 * and the leave/operation lookup helpers used by the grid cells.
 */
export function useRosterGrid({ roster, setRoster, rosterId, employees, shifts, timeOff, leaveTypes, operations, revalidate }) {
  const [calendarView, setCalendarView] = useState("full"); // week, month, full, police, timeline, heatmap, daycards
  const [weekOffset, setWeekOffset] = useState(0);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false);
  const [dayCardDate, setDayCardDate] = useState(null);
  const [dragData, setDragData] = useState(null);
  const [addEntryDialog, setAddEntryDialog] = useState(null);

  const currentLayout = ROSTER_LAYOUTS.find((l) => l.id === calendarView) || ROSTER_LAYOUTS[2];

  // Load the user's preferred layout on mount and persist on change.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/settings`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const pref = r.data?.layout_preference;
        if (pref && ROSTER_LAYOUTS.find((l) => l.id === pref)) {
          setCalendarView(pref);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLayoutLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!layoutLoaded) return;
    axios
      .put(`${API}/settings`, { layout_preference: calendarView }, { withCredentials: true })
      .catch(() => { /* swallow — preference is non-critical */ });
  }, [calendarView, layoutLoaded]);

  const getDatesInRange = () => {
    if (!roster?.start_date || !roster?.end_date) return [];
    try {
      return eachDayOfInterval({ start: parseISO(roster.start_date), end: parseISO(roster.end_date) });
    } catch {
      return [];
    }
  };
  const dates = getDatesInRange();

  const getVisibleDates = () => {
    if (!dates.length) return [];
    if (calendarView === "full") return dates;
    if (calendarView === "month") {
      const start = weekOffset * 28;
      return dates.slice(start, start + 28);
    }
    const start = weekOffset * 7;
    return dates.slice(start, start + 7);
  };
  const visibleDates = getVisibleDates();
  const totalWeeks = Math.ceil(dates.length / 7);
  const totalMonths = Math.ceil(dates.length / 28);
  const maxOffset = calendarView === "week" ? totalWeeks - 1 : calendarView === "month" ? totalMonths - 1 : 0;

  const getViewLabel = () => {
    if (!visibleDates.length) return "";
    const first = visibleDates[0];
    const last = visibleDates[visibleDates.length - 1];
    return `${fmtDayMonth(first)} — ${fmtDateMed(last)}`;
  };

  const getEntriesForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return roster?.entries?.filter((e) => e.date === dateStr) || [];
  };

  const getEmployeeName = (employeeId) => employees.find((e) => e.employee_id === employeeId)?.name || "Unknown";
  const getShift = (shiftId) => shifts.find((s) => s.shift_id === shiftId);
  const getInitials = (name) => (!name ? "?" : name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2));

  // Map a leave type key to a short display badge (A/L, SICK, RDO, PERS, UNP, OFF)
  const leaveBadge = (key) => {
    const k = (key || "annual").toLowerCase();
    if (k === "annual") return { label: "A/L", bg: "#A78BFA" };
    if (k === "sick") return { label: "SICK", bg: "#F87171" };
    if (k === "rdo") return { label: "RDO", bg: "#34D399" };
    if (k === "personal") return { label: "PERS", bg: "#60A5FA" };
    if (k === "unpaid") return { label: "UNP", bg: "#9CA3AF" };
    return { label: "OFF", bg: "#FBBF24" };
  };

  // Return the approved leave (if any) overlapping date for the employee.
  const getLeaveForCell = (employeeId, dateStr) => {
    if (!timeOff || timeOff.length === 0) return null;
    return timeOff.find(
      (t) => t.employee_id === employeeId && t.status === "approved" && t.start_date <= dateStr && t.end_date >= dateStr,
    );
  };

  // Return the operation (if any) covering this (date, shift_id) tuple.
  // Hard ops take priority over soft ops; among same priority, first match wins.
  const operationForSlot = (dateStr, shiftId) => {
    if (!operations || operations.length === 0) return null;
    let bestHard = null;
    let bestSoft = null;
    for (const op of operations) {
      if (op.shift_id !== shiftId) continue;
      if (!op.dates.includes(dateStr)) continue;
      if (op.is_hard && !bestHard) bestHard = op;
      else if (!op.is_hard && !bestSoft) bestSoft = op;
    }
    return bestHard || bestSoft;
  };

  // Count CREDITED leave days for an employee within the roster window —
  // mirrors the engine's `_phase_pre_apply_leave_credits`:
  //  • only leave types with counts_toward_work_days earn credit
  //  • explicit `work_days_counted` caps the credit for that request
  //  • blank means "auto-count weekdays in the range"
  const leaveTypeCounts = (key) => {
    const k = (key || "annual").toLowerCase();
    if (leaveTypes) {
      const lt = leaveTypes.find((t) => (t.key || "").toLowerCase() === k);
      return lt ? !!lt.counts_toward_work_days : false;
    }
    return !!LEAVE_COUNTS_FALLBACK[k];
  };

  const countLeaveDaysForEmployee = (employeeId) => {
    if (!timeOff || !roster?.start_date || !roster?.end_date) return 0;
    let count = 0;
    for (const t of timeOff) {
      if (t.employee_id !== employeeId || t.status !== "approved") continue;
      if (!leaveTypeCounts(t.leave_type)) continue;
      const a = t.start_date > roster.start_date ? t.start_date : roster.start_date;
      const b = t.end_date < roster.end_date ? t.end_date : roster.end_date;
      if (a > b) continue;
      let overlapDays = 0;
      let weekdays = 0;
      for (let d = parseYMD(a); d <= parseYMD(b); d.setDate(d.getDate() + 1)) {
        overlapDays += 1;
        if (d.getDay() !== 0 && d.getDay() !== 6) weekdays += 1;
      }
      const wdc = t.work_days_counted;
      if (wdc !== null && wdc !== undefined && Number.isFinite(Number(wdc))) {
        count += Math.min(Number(wdc), overlapDays);
      } else {
        count += weekdays;
      }
    }
    return count;
  };

  const handleDragStart = (e, entry) => {
    setDragData(entry);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", entry.entry_id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, targetEmployeeId, targetDate) => {
    e.preventDefault();
    if (!dragData) return;
    const dateStr = format(targetDate, "yyyy-MM-dd");
    const updatedEntries = roster.entries.map((entry) => {
      if (entry.entry_id === dragData.entry_id) {
        // Manual move = lock it so future regenerate keeps it
        return { ...entry, employee_id: targetEmployeeId, date: dateStr, is_locked: true };
      }
      return entry;
    });
    try {
      const response = await axios.put(`${API}/rosters/${rosterId}/entries`, { entries: updatedEntries }, { withCredentials: true });
      setRoster(response.data);
      toast.success("Shift reassigned");
      revalidate();
    } catch (error) {
      console.error("Failed to reassign:", error);
      toast.error("Failed to reassign shift");
    }
    setDragData(null);
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      const response = await axios.delete(`${API}/rosters/${rosterId}/entries/${entryId}`, { withCredentials: true });
      setRoster(response.data);
      toast.success("Entry removed");
      revalidate();
    } catch (error) {
      toast.error("Failed to remove entry");
    }
  };

  const handleAddEntry = async (employeeId, date, shiftId) => {
    try {
      const response = await axios.post(`${API}/rosters/${rosterId}/entries`, {
        entry_id: "",
        employee_id: employeeId,
        shift_id: shiftId,
        date: date,
        notes: null,
      }, { withCredentials: true });
      setRoster(response.data);
      setAddEntryDialog(null);
      toast.success("Entry added");
      revalidate();
    } catch (error) {
      toast.error("Failed to add entry");
    }
  };

  return {
    calendarView, setCalendarView,
    weekOffset, setWeekOffset,
    layoutPickerOpen, setLayoutPickerOpen,
    dayCardDate, setDayCardDate,
    currentLayout,
    dates, visibleDates, totalWeeks, totalMonths, maxOffset, getViewLabel,
    dragData, handleDragStart, handleDragOver, handleDrop,
    getEntriesForDate, getEmployeeName, getShift, getInitials,
    leaveBadge, getLeaveForCell, operationForSlot, countLeaveDaysForEmployee,
    handleDeleteEntry, handleAddEntry,
    addEntryDialog, setAddEntryDialog,
  };
}
